/**
 * Constrains a value to not exceed the specified bounds.
 *
 * @param value - the value to be constrained
 * @param min - the lower bound
 * @param max - the upper bound
 * @param [prioritizeMax=false] - Specifies if max should be returned in the case that max is smaller than min. Otherwise min is returned.
 *
 * @returns the constraint value
 */
export function constrain(
  value: number,
  min: number,
  max: number,
  prioritizeMax = false
): number {
  // This logic ignores value because when max is less than min there is a 'negative' range that value simply cannot exist within
  if (max < min) {
    if (prioritizeMax) {
      return max;
    }
    return min;
  }

  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
}

/**
 * Restricts a value to be within set bounds. The value will be clamped to the range with a modulo,
 * meaning that if the value is outside of it, it will be shifted into the range.
 *
 * moduloClamp(0, 1, 3) => 2
 * moduloClamp(1, 1, 3) => 1
 * moduloClamp(2, 1, 3) => 2
 * moduloClamp(3, 1, 3) => 1
 * moduloClamp(1, 1, 3) => 1
 *
 * @param value The value to be clamped
 * @param min The lower bound (inclusive)
 * @param max The upper bound (non inclusive)
 */
export function moduloClamp(value: number, min: number, max: number): number {
  return mod(value - min, max - min) + min;
}

export function mod(value: number, divisor: number): number {
  const result = value % divisor;
  return result < 0 ? result + divisor : result;
}
